import { useImagingOrder } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { imagingOrderFormSchema, type ImagingOrderFormValues } from '@schema/imagingOrder.schema'
import type {
  ImagingOrderData,
  ImagingOrderIncludedOrder,
  ImagingOrderIncludedResource,
  ImagingOrderUpdatePayload
} from '@type/imaging_order.type'
import type { OrderImagingIncluded } from '@type/order.type'
import { FormButton, Modal, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { buildTestsAttribute, newFreeTextOption, searchTests, type TestLineOption } from '@util/testCatalogue'
import { useCallback, useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import type { GroupBase } from 'react-select'
import { buildImagingPlacedLines } from './ImagingDetails.helpers'
import { OptionTag, TestLineRows } from './TestLineRows'

type Props = {
  onClose: () => void
  id: string
  defaultData?: ImagingOrderData
  included?: ImagingOrderIncludedResource[]
}

/** Existing lines as picker options: facility studies keep their id, free text its name; both keep `performed_at`. */
export function studiesFromOrder(
  order: ImagingOrderData,
  included: ImagingOrderIncludedResource[] | undefined
): TestLineOption[] {
  const lines = buildImagingPlacedLines(order, included)
  return lines.map(line => {
    if (!line.orderId) {
      return { label: line.label, performed_at: line.performedAt, source: 'free_text', value: line.label }
    }
    const child = included?.find((x): x is ImagingOrderIncludedOrder => x.type === 'order' && x.id === line.orderId)
    const orderable = child?.relationships?.orderable
    const imagingIdFromRel =
      orderable && typeof orderable === 'object' && 'data' in orderable
        ? (orderable.data as { id?: string } | null | undefined)?.id
        : undefined
    const imagingId =
      imagingIdFromRel != null
        ? Number(imagingIdFromRel)
        : child?.attributes.orderable_id != null
          ? Number(child.attributes.orderable_id)
          : NaN
    const imaging = Number.isFinite(imagingId)
      ? included?.find((x): x is OrderImagingIncluded => x.type === 'imaging' && String(x.id) === String(imagingId))
      : undefined
    const label = imaging?.attributes.study_type ?? line.label
    // Keep a resolvable facility id; never use NaN (that would drop the line as free text on save).
    if (Number.isFinite(imagingId)) {
      return { label, performed_at: line.performedAt, source: 'facility', value: imagingId }
    }
    return { label, performed_at: line.performedAt, source: 'free_text', value: label }
  })
}

const EditImagingOrder = ({ onClose, id, defaultData, included }: Props) => {
  const { singleImagingOrder, updateImagingOrder } = useImagingOrder({ id })
  const order = (singleImagingOrder?.data?.type === 'imaging_order' ? singleImagingOrder.data : defaultData) as
    | ImagingOrderData
    | undefined
  const resolvedIncluded = singleImagingOrder?.included ?? included

  const defaultStudies = useMemo(
    () => (order ? studiesFromOrder(order, resolvedIncluded) : []),
    [order, resolvedIncluded]
  )

  const methods = useForm<ImagingOrderFormValues>({
    defaultValues: { imaging_studies: [], note: order?.attributes.note ?? '' },
    resolver: yupResolver(imagingOrderFormSchema)
  })
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = methods

  useEffect(() => {
    if (!order) return
    reset({
      imaging_studies: defaultStudies,
      note: order.attributes.note ?? ''
    })
  }, [defaultStudies, order, reset])

  const loadImagingOptions = useCallback((inputValue: string) => searchTests('imaging', inputValue), [])

  const onSubmit = async (form: ImagingOrderFormValues) => {
    if (!id) return
    if (!order?.attributes.study_names_editable) {
      showToast('Studies can no longer be edited on this order')
      return
    }

    const payload: ImagingOrderUpdatePayload = {
      data: {
        type: 'imaging_order',
        attributes: {
          note: form.note?.trim() ?? '',
          tests: buildTestsAttribute(form.imaging_studies, 'imaging')
        }
      }
    }

    try {
      await updateImagingOrder({ formVal: payload, id })
      showToast('Imaging studies updated')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Edit studies">
        <form className="flex min-w-[530px] flex-col gap-4 overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4 overflow-auto p-6">
            <Controller
              control={control}
              name="imaging_studies"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Select<TestLineOption, true, GroupBase<TestLineOption>>
                    borderRadius={'15px'}
                    defaultOptions
                    error={error?.message}
                    formatOptionLabel={(option, { context }) => (
                      <span>
                        {option.label}
                        {context === 'menu' ? <OptionTag option={option} /> : null}
                      </span>
                    )}
                    getNewOptionData={(inputValue, optionLabel) => newFreeTextOption(inputValue, optionLabel)}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => String(o.value)}
                    isAsync
                    isCreatable
                    isMulti
                    label="Study"
                    loadOptions={loadImagingOptions}
                    onChange={opts => onChange((opts as TestLineOption[] | null) ?? [])}
                    placeholder="Search studies (e.g. chest X-ray) or type a name"
                    value={value as TestLineOption[]}
                  />
                  <TestLineRows kind="imaging" lines={(value as TestLineOption[]) ?? []} onChange={onChange} />
                </>
              )}
            />

            <TextAreaField label="Note to radiology" name="note" placeholder="Enter Note" />
          </div>
          <FormButton isLoading={isSubmitting} onClose={onClose} submit="Save" />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default EditImagingOrder
